"use client";
import React, { useState } from "react";
import type { Course } from "@/components/CourseCard";

export default function RegisterModal({ course, onClose }: { course: Course | null; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  if (!course) return null;

  async function handleSubmit(formData: FormData) {
    setLoading(true);
    setError(null);
    setOk(false);
    try {
      const privacy = formData.get("privacyAccepted");
      if (!privacy) {
        setError("Debes aceptar la política de privacidad");
        setLoading(false);
        return;
      }

      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student: formData.get("name"),
          email: formData.get("email"),
          phone: formData.get("phone"),
          academicDegree: formData.get("academicDegree"),
          message: formData.get("message"),
          course: course?.title,
          courseId: course?.id,
          dardeActive: formData.get("dardeActive"),
          privacyAccepted: true,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Error");
      setOk(true);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Error";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-slate-800">Inscripción: {course.title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">✕</button>
        </div>
        {ok ? (
          <div className="py-10 text-center">
            <h4 className="text-xl font-semibold text-green-600">¡Inscripción enviada!</h4>
            <p className="mt-2 text-slate-600">Gracias por tu interés. Nos pondremos en contacto contigo pronto.</p>
            <button onClick={onClose} className="mt-6 btn-primary">Cerrar</button>
          </div>
        ) : (
          <form
            action={handleSubmit}
            className="mt-6 grid grid-cols-1 gap-4"
          >
            <input name="name" required placeholder="Nombre completo" className="input-field" />
            <input name="email" type="email" required placeholder="Email" className="input-field" />
            <input name="phone" placeholder="Teléfono" className="input-field" />
            <input name="academicDegree" placeholder="Titulación académica" className="input-field" />
            <textarea name="message" placeholder="¿Por qué te interesa este curso?" rows={4} className="input-field" />
            <div className="card">
              <p className="font-semibold text-slate-800">¿Tiene el DARDE activo?</p>
              <div className="mt-2 flex gap-6">
                <label className="flex items-center gap-2">
                  <input type="radio" name="dardeActive" value="Sí" required />
                  <span>Sí</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="dardeActive" value="No" />
                  <span>No</span>
                </label>
              </div>
            </div>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <input type="checkbox" name="privacyAccepted" required className="mt-1" />
              <span>
                He leído y acepto la <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">Política de Privacidad</a>.
              </span>
            </label>
            <div className="flex items-center justify-end gap-4 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? "Enviando..." : "Confirmar inscripción"}
              </button>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">Error: {error}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
